import { useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  type SecurityMode,
  useClearRegionSecurityPolicy,
  useRegionSecurityPolicy,
  useSetRegionSecurityPolicy,
} from '@/hooks/useSecurityPolicy';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import SecurityModeSelector from './SecurityModeSelector';

interface Props {
  regionId: string;
}

const INHERITED_SENTINEL = '00000000-0000-0000-0000-000000000000';

/**
 * Per-region default port security mode picker. Embedded inside
 * RegionDetails > Overview tab.
 *
 * Backend returns the inherited mode with `id = nil-uuid` when no
 * explicit region row exists. We use that to tell "inherited" from
 * "explicit override" — selecting "(inherit)" calls the clear
 * mutation; selecting a specific mode calls set.
 */
export default function RegionSecurityPolicySection({ regionId }: Props) {
  const { data, isLoading } = useRegionSecurityPolicy(regionId);
  const setMutation = useSetRegionSecurityPolicy(regionId);
  const clearMutation = useClearRegionSecurityPolicy(regionId);

  const isExplicit = data ? data.id !== INHERITED_SENTINEL : false;
  const [selected, setSelected] = useState<SecurityMode | null>(null);

  // Seed from the server value on load and whenever it changes (render-time, not an effect).
  // `isExplicit` derives from `data`, so tracking `data` covers both.
  const [synced, setSynced] = useState<typeof data>(undefined);
  if (data !== synced) {
    setSynced(data);
    if (data) setSelected(isExplicit ? data.defaultPortSecurityMode : null);
  }

  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (!data) return null;

  const inheritedMode = !isExplicit ? data.defaultPortSecurityMode : null;

  const handleSave = () => {
    if (selected === null) {
      clearMutation.mutate();
    } else {
      setMutation.mutate(selected);
    }
  };

  const pending = setMutation.isPending || clearMutation.isPending;
  const errored = setMutation.isError || clearMutation.isError;
  const succeeded = setMutation.isSuccess || clearMutation.isSuccess;

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <h3 className="text-base font-semibold mb-1">Default port security mode</h3>
      <p className="text-sm text-muted-foreground mb-3">
        Applied to ports on devices in this region that don't have their own setting. Falls
        back to the organization default when not set.
      </p>
      {inheritedMode ? (
        <p className="text-xs text-muted-foreground mb-3">
          Currently inheriting <code className="font-mono">{inheritedMode}</code> from the
          organization.
        </p>
      ) : null}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <SecurityModeSelector value={selected} onChange={setSelected} allowInherit />
        <Button onClick={handleSave} disabled={pending}>
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {errored ? (
        <p className="text-xs text-destructive mt-2 inline-flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" />
          Save failed.
        </p>
      ) : null}
      {succeeded ? (
        <p className="text-xs text-sev-4 mt-2 inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Saved.
        </p>
      ) : null}
    </section>
  );
}
